'use client';

/**
 * DataRoomBrowsePanel Component
 *
 * Browse and link panel for adding documents to the data room.
 * Shows a list of sources on the left and documents for the selected source on the right.
 */

import CheckCircle from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton/GlassButton';
import { GlassChip } from '../GlassChip/GlassChip';
import { type DocumentScope, DocumentScopeBadge } from './DocumentScopeBadge';

// ============================================================================
// Types
// ============================================================================

export interface BrowseDocument {
  /** Document identifier */
  documentId: string;
  /** Document display name */
  name: string;
  /** Document type label */
  documentType: string;
  /** Document scope */
  scope: DocumentScope;
  /** Whether this document is already linked */
  alreadyLinked: boolean;
}

export interface BrowseSource {
  /** Source identifier */
  sourceId: string;
  /** Source display name */
  sourceName: string;
  /** Source label (e.g., "Primary Borrower") */
  sourceLabel: string;
  /** Documents available from this source */
  documents: BrowseDocument[];
}

export interface DataRoomBrowsePanelProps {
  /** Available sources to browse */
  sources: BrowseSource[];
  /** Callback when a document is linked */
  onLink: (documentId: string, sourceId: string) => void;
  /** Callback for bulk linking multiple documents */
  onBulkLink?: (documentIds: string[], sourceId: string) => void;
  /** Currently selected source ID */
  selectedSourceId?: string;
  /** Callback when a source is selected */
  onSelectSource: (sourceId: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export const DataRoomBrowsePanel = ({
  sources,
  onLink,
  onBulkLink: _onBulkLink,
  selectedSourceId,
  onSelectSource,
}: DataRoomBrowsePanelProps): React.ReactElement => {
  const { t } = useI18n();

  const selectedSource = sources.find((s) => s.sourceId === selectedSourceId);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        minHeight: 300,
      }}
    >
      {/* Source list (left side) */}
      <Box
        sx={{
          width: 240,
          minWidth: 200,
          borderRight: 1,
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            px: 2,
            pt: 1.5,
            pb: 0.5,
            display: 'block',
          }}
        >
          {t('dataRoom.sources')}
        </Typography>
        <List dense disablePadding>
          {sources.map((source) => {
            const availableCount = source.documents.filter((d) => !d.alreadyLinked).length;
            return (
              <ListItemButton
                key={source.sourceId}
                selected={source.sourceId === selectedSourceId}
                onClick={() => onSelectSource(source.sourceId)}
                sx={{ px: 2 }}
              >
                <ListItemText
                  primary={source.sourceName}
                  secondary={source.sourceLabel}
                  slotProps={{
                    primary: { variant: 'body2' },
                    secondary: { variant: 'caption' },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {availableCount}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Box>
      <Divider orientation="vertical" flexItem />
      {/* Document list (right side) */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {selectedSource ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {selectedSource.sourceName}
            </Typography>
            {selectedSource.documents.map((doc) => (
              <Box
                key={doc.documentId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {doc.name}
                    </Typography>
                    <DocumentScopeBadge scope={doc.scope} />
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {doc.documentType}
                  </Typography>
                </Stack>

                {doc.alreadyLinked ? (
                  <GlassChip
                    icon={<CheckCircle sx={{ fontSize: iconSizes.sm }} />}
                    label={t('dataRoom.reuse.alreadyLinked')}
                    color="success"
                    size="small"
                    variant="outlined"
                    glowOnHover={false}
                  />
                ) : (
                  <GlassButton
                    variant="outlined"
                    size="small"
                    onClick={() => onLink(doc.documentId, selectedSource.sourceId)}
                  >
                    {t('dataRoom.linkDocument')}
                  </GlassButton>
                )}
              </Box>
            ))}
            {selectedSource.documents.length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  py: 4,
                  textAlign: 'center',
                }}
              >
                {t('dataRoom.empty.title')}
              </Typography>
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.disabled',
              }}
            >
              {t('dataRoom.sources')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

DataRoomBrowsePanel.displayName = 'DataRoomBrowsePanel';

export default DataRoomBrowsePanel;
